// Dashboard.jsx – User Dashboard with Recharts, spatial filters, and date selection
import React, { useEffect, useMemo, useState } from "react";
import { Switch, FormControlLabel } from "@mui/material";
import "./Dashboard.css";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  Download,
  Print,
  CalendarMonth,
  CheckCircle,
  Refresh,
  FilterList,
} from "@mui/icons-material";
import moment from "moment-jalaali";
import TimeFilterDialog from "../components/TimeFilterDialog";
import SpatialFilterModal from "../components/SpatialFilterModal";
import {
  fetchDashboardStats,
  fetchComparisonChart,
  fetchDoubleComparisonChart,
  fetchTableData,
  fetchDashboardTimeSeries,
} from "../api/dashboardApi";
import {
  fetchSpatialFilters,
  fetchSpatialOptions,
  fetchSpatialUnits,
  fetchSpatialLevels,
} from "../api/spatialApi";

const PRODUCTION_LABELS = {
  farm: "مزرعه",
  block: "بلوک",
  office: "اداره",
  management: "مدیریت",
};

const DEFAULT_PRODUCTION_OPTIONS = [
  { value: "farm", label: PRODUCTION_LABELS.farm },
  { value: "block", label: PRODUCTION_LABELS.block },
  { value: "office", label: PRODUCTION_LABELS.office },
  { value: "management", label: PRODUCTION_LABELS.management },
];

// ✅ Updated to match backend documentation
const indicatorOptions = [
  { value: "NDVI", label: "NDVI" },
  { value: "EVI", label: "EVI" },
  { value: "SAVI", label: "SAVI" },
  { value: "NDWI", label: "NDWI" },
  { value: "MNDWI", label: "MNDWI" },
  { value: "LST", label: "LST" },
  { value: "TA", label: "TA" },
  { value: "CWSI_THERMAL", label: "CWSI (حرارتی)" },
  { value: "CWSI_OPTIC", label: "CWSI (نوری)" },
  { value: "OPTRAM", label: "OPTRAM" },
  { value: "METRIC", label: "METRIC" },
  { value: "GPP", label: "GPP" },
  { value: "FAPAR", label: "FAPAR" },
  { value: "LAI", label: "LAI" },
];

const productionOptions = [
  { value: "farm", label: "مزرعه" },
  { value: "block", label: "بلوک" },
  { value: "office", label: "اداره" },
  { value: "management", label: "مدیریت" },
];

const defaultEndDate = moment();
const defaultStartDate = moment().subtract(1, "year");

const initialFilters = {
  keshte: "",
  production: "farm",
  department: "",
  productType: "",
  number: "",
  age: "",
  status: "",
  searchQuery: "",
};

const quickDateRanges = [
  {
    label: "آخرین ماه",
    start: moment().subtract(1, "month"),
    end: moment(),
  },
  {
    label: "سه ماهه",
    start: moment().subtract(3, "month"),
    end: moment(),
  },
  {
    label: "شش ماهه",
    start: moment().subtract(6, "month"),
    end: moment(),
  },
  {
    label: "آخرین سال",
    start: moment().subtract(1, "year"),
    end: moment(),
  },
];

const Dashboard = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [keshteOptions, setKeshteOptions] = useState([]);
  const [croptypeOptions, setCroptypeOptions] = useState([]);
  const [productionOptionsState, setProductionOptionsState] = useState(DEFAULT_PRODUCTION_OPTIONS);
  const [varietyOptions, setVarietyOptions] = useState([]);
  const [ageOptions, setAgeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [primaryIndicator, setPrimaryIndicator] = useState("NDVI");
  const [secondaryIndicator, setSecondaryIndicator] = useState("NDWI");
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [isTimeFilterOpen, setIsTimeFilterOpen] = useState(false);

  const [mockEnabled, setMockEnabled] = useState(() =>
    String(localStorage.getItem("useMockApi") || "true") === "true",
  );

  const [stats, setStats] = useState({});
  const [comparisonData, setComparisonData] = useState([]);
  const [doubleData, setDoubleData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [isSpatialModalOpen, setIsSpatialModalOpen] = useState(false);
  const [selectedSpatialFilters, setSelectedSpatialFilters] = useState(null);
  const [selectedSpatialGeoJSON, setSelectedSpatialGeoJSON] = useState(null);

  const showDepartment = filters.production === "office" || filters.production === "management";
  const showSearch = filters.production === "block" || filters.production === "farm";

  useEffect(() => {
    moment.loadPersian({ dialect: "persian-modern", usePersianDigits: false });
  }, []);

  useEffect(() => {
    loadSpatialFilters();
    loadDashboardData();
  }, []);

  const buildProductionOptions = (types = []) => {
    if (!Array.isArray(types) || types.length === 0) return DEFAULT_PRODUCTION_OPTIONS;
    return types.map((type) => ({
      value: type,
      label: PRODUCTION_LABELS[type] || type,
    }));
  };

  const loadSpatialFilters = async () => {
    try {
      const data = await fetchSpatialFilters();
      setKeshteOptions(data.keshte || []);
      setCroptypeOptions(data.croptype || []);
      setProductionOptionsState(
        buildProductionOptions(data.types || data.available_unit_types)
      );
    } catch (error) {
      console.error("Unable to load spatial filter values:", error);
      setKeshteOptions([]);
      setCroptypeOptions([]);
      setProductionOptionsState(DEFAULT_PRODUCTION_OPTIONS);
    }
  };

  useEffect(() => {
    if (!filters.keshte) {
      setVarietyOptions([]);
      setAgeOptions([]);
      setStatusOptions([]);
      return;
    }

    const loadOptions = async () => {
      try {
        const data = await fetchSpatialOptions(filters.keshte, filters.productType);
        setVarietyOptions(data.variety || []);
        setAgeOptions(data.age || []);
        setStatusOptions(data.status || []);
      } catch (error) {
        console.error("Spatial options load failed:", error);
        setVarietyOptions([]);
        setAgeOptions([]);
        setStatusOptions([]);
      }
    };

    loadOptions();
  }, [filters.keshte, filters.productType]);

  useEffect(() => {
    if (!filters.keshte || !filters.production) {
      setUnitOptions([]);
      return;
    }

    const loadUnits = async () => {
      try {
        const data = await fetchSpatialUnits({
          keshte: filters.keshte,
          type: filters.production,
          croptype: filters.productType,
          variety: filters.number,
          age: filters.age,
          status: filters.status,
        });
        setUnitOptions(data.units || []);
      } catch (error) {
        console.error("Spatial units load failed:", error);
        setUnitOptions([]);
      }
    };

    loadUnits();
  }, [filters.keshte, filters.production, filters.productType, filters.number, filters.age, filters.status]);

  const buildQuery = ({ filters: queryFilters = filters, startDate: queryStartDate = startDate, endDate: queryEndDate = endDate } = {}) => {
    const payload = {};
    if (queryFilters.keshte) payload.keshte = queryFilters.keshte;
    if (queryFilters.production) payload.type = queryFilters.production;
    if (queryFilters.department) payload.unit = queryFilters.department;
    else if (queryFilters.searchQuery) payload.unit = queryFilters.searchQuery;
    if (queryFilters.productType) payload.croptype = queryFilters.productType;
    if (queryFilters.number) payload.variety = queryFilters.number;
    if (queryFilters.age) payload.age = queryFilters.age;
    if (queryFilters.status) payload.status = queryFilters.status;
    if (queryStartDate) payload.date_from = queryStartDate.format("YYYY-MM-DD");
    if (queryEndDate) payload.date_to = queryEndDate.format("YYYY-MM-DD");
    return payload;
  };

  const normalizeIndicatorData = (items, key) =>
    (Array.isArray(items) ? items : []).map((item) => ({
      name: item.name || item.keshte || item.unit || "—",
      [key]: item[key] ?? item.value ?? item.nvalue ?? 0,
      ...item,
    }));

  const loadDashboardData = async ({
    filters: queryFilters,
    startDate: queryStartDate,
    endDate: queryEndDate,
    primaryIndicator: queryPrimaryIndicator,
    secondaryIndicator: querySecondaryIndicator,
  } = {}) => {
    setIsLoading(true);
    const effectivePrimary = queryPrimaryIndicator ?? primaryIndicator;
    const effectiveSecondary = querySecondaryIndicator ?? secondaryIndicator;
    const filtersPayload = buildQuery({ filters: queryFilters, startDate: queryStartDate, endDate: queryEndDate });

    try {
      const [statsResult, comparisonResult, doubleResult, tableResult, seriesResult] = await Promise.all([
        fetchDashboardStats(filtersPayload),
        fetchComparisonChart(effectivePrimary, filtersPayload),
        fetchDoubleComparisonChart(effectivePrimary, effectiveSecondary, filtersPayload),
        fetchTableData({ indicators: `${effectivePrimary},${effectiveSecondary}`, ...filtersPayload }),
        fetchDashboardTimeSeries(effectivePrimary, filtersPayload),
      ]);

      setStats(statsResult || {});

      // Single comparison
      setComparisonData(normalizeIndicatorData(comparisonResult, effectivePrimary));

      // Double comparison – preserve missing values as null
      const doubleNormalized = normalizeIndicatorData(doubleResult, effectivePrimary);
      const doubleWithSecondary = doubleNormalized.map((item, index) => ({
        ...item,
        [effectiveSecondary]: Array.isArray(doubleResult)
          ? doubleResult[index]?.[effectiveSecondary] ?? null
          : null,
      }));
      setDoubleData(doubleWithSecondary);

      // Table data – keys from API are lowercased, we keep as-is
      setTableData(Array.isArray(tableResult) ? tableResult : []);

      // Time series
      setTimeSeriesData(
        (Array.isArray(seriesResult) ? seriesResult : []).map((item) => ({
          label: item.month || item.date || item.label || "—",
          value: item.value ?? item.val ?? 0,
        }))
      );
    } catch (error) {
      console.error("Dashboard data load failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrimaryIndicatorChange = (value) => {
    if (value === secondaryIndicator) {
      const fallback = indicatorOptions.find((item) => item.value !== value)?.value;
      if (fallback) setSecondaryIndicator(fallback);
    }
    setPrimaryIndicator(value);
  };

  const handleSecondaryIndicatorChange = (value) => {
    if (value === primaryIndicator) {
      const fallback = indicatorOptions.find((item) => item.value !== value)?.value;
      if (fallback) setPrimaryIndicator(fallback);
    }
    setSecondaryIndicator(value);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "keshte"
        ? { department: "", searchQuery: "", number: "", age: "", status: "", productType: "" }
        : {}),
      ...(field === "production" ? { department: "", searchQuery: "" } : {}),
    }));
  };

  const handleReset = () => {
    const resetFilters = initialFilters;
    const resetStart = defaultStartDate;
    const resetEnd = defaultEndDate;
    setFilters(resetFilters);
    setStartDate(resetStart);
    setEndDate(resetEnd);
    setPrimaryIndicator("NDVI");
    setSecondaryIndicator("NDWI");
    loadDashboardData({
      filters: resetFilters,
      startDate: resetStart,
      endDate: resetEnd,
      primaryIndicator: "NDVI",
      secondaryIndicator: "NDWI",
    });
  };

  const handleQuickDateRange = (range) => {
    setStartDate(range.start);
    setEndDate(range.end);
    loadDashboardData({ startDate: range.start, endDate: range.end });
  };

  const handleDashboardSpatialFilterApply = (payload) => {
    if (!payload) {
      setSelectedSpatialFilters(null);
      setSelectedSpatialGeoJSON(null);
      setFilters((prev) => ({
        ...prev,
        department: "",
        searchQuery: "",
      }));
      loadDashboardData();
      return;
    }

    const appliedFilters = payload.filters || payload;
    const appliedGeoJSON = payload.geoJSON || null;

    setSelectedSpatialFilters(appliedFilters);
    setSelectedSpatialGeoJSON(appliedGeoJSON);
    setFilters((prev) => ({ ...prev, ...appliedFilters }));
    setIsSpatialModalOpen(false);
    loadDashboardData({ filters: { ...filters, ...appliedFilters } });
  };

  // Updated CSV download to match the actual table columns
  const downloadCsv = () => {
    if (!tableData.length) return;
    const headerRow = ["کشت و صنعت", primaryIndicator, secondaryIndicator];
    const rows = [
      headerRow,
      ...tableData.map((row) => [
        row.name || "",
        row[primaryIndicator.toLowerCase()] ?? "",
        row[secondaryIndicator.toLowerCase()] ?? "",
      ]),
    ];
    const csv = rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `dashboard_table_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleTimeFilterConfirm = () => {
    setIsTimeFilterOpen(false);
    loadDashboardData();
  };

  const handleTimeFilterClear = () => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
  };

  const dateLabel = useMemo(() => {
    if (!startDate || !endDate) return "آخرین سال";
    return `${startDate.format("jYYYY/jMM/jDD")} تا ${endDate.format("jYYYY/jMM/jDD")}`;
  }, [startDate, endDate]);

  const spatialLabel = useMemo(() => {
    if (!filters.keshte && !filters.productType && !filters.department && !filters.searchQuery) {
      return "همه مزارع";
    }
    const parts = [];
    if (filters.keshte) parts.push(filters.keshte);
    if (filters.production) {
      const option = productionOptions.find((item) => item.value === filters.production)?.label;
      if (option) parts.push(option);
    }
    if (filters.productType) parts.push(filters.productType);
    if (filters.searchQuery) parts.push(filters.searchQuery);
    if (filters.department) parts.push(filters.department);
    return parts.join(" · ");
  }, [filters]);

  const statCards = [
    { label: "رطوبت نسبی", value: stats.humidity ?? "—", percent: stats.humidity ?? 0, icon: "💧" },
    { label: "تبخیر تعرق", value: stats.evapotranspiration ?? "—", percent: stats.evapotranspiration ? Math.min(100, stats.evapotranspiration * 10) : 0, icon: "🌫" },
    { label: "بارش", value: stats.rainfall ?? "—", percent: stats.rainfall ? Math.min(100, stats.rainfall * 4) : 0, icon: "🌧️" },
    { label: "دما", value: stats.temperature ?? "—", percent: stats.temperature ? Math.min(100, stats.temperature + 20) : 0, icon: "🌡️" },
    { label: "رطوبت خاک", value: stats.soil_moisture ?? "—", percent: stats.soil_moisture ?? 0, icon: "🌱" },
    { label: "تنش آبی", value: stats.water_stress ?? "—", percent: stats.water_stress ? Math.min(100, stats.water_stress * 100) : 0, icon: "💦" },
    { label: "نیتروژن برگ", value: stats.leaf_nitrogen ?? "—", percent: stats.leaf_nitrogen ? Math.min(100, stats.leaf_nitrogen * 20) : 0, icon: "🍃" },
  ];

  return (
    <div className="dashboard" dir="rtl">
      <section className="section-card dashboard-filter-card">
        <div className="section-header">
          <div className="header-title-group">
            <div className="title-icon">📌</div>
            <h2 className="section-title">فیلتر گزارش داشبورد</h2>
          </div>
          <div className="action-buttons">
            <FormControlLabel
              control={
                <Switch
                  checked={mockEnabled}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setMockEnabled(val);
                    localStorage.setItem("useMockApi", val ? "true" : "false");
                    try {
                      // refresh dashboard data to reflect mock toggle
                      loadDashboardData();
                    } catch (err) {
                      // loadDashboardData may not be available in some contexts; ignore
                    }
                  }}
                  size="small"
                />
              }
              label="استفاده از داده‌های آزمایشی"
              labelPlacement="start"
            />
            <Button
              variant="outlined"
              className="filter-action-button"
              onClick={handleReset}
              startIcon={<Refresh />}
            >
              پاکسازی
            </Button>
            <Button
              variant="outlined"
              className="filter-action-button"
              onClick={() => setIsSpatialModalOpen(true)}
              startIcon={<FilterList />}
            >
              فیلتر مکانی
            </Button>
            <Button
              variant="contained"
              className="primary-action-button"
              onClick={loadDashboardData}
              startIcon={<CheckCircle />}
            >
              بارگذاری
            </Button>
          </div>
        </div>

        <div className="section-content">
          <div className="dashboard-filter-grid">
            <TextField
              variant="outlined"
              size="small"
              select
              label="کشت و صنعت"
              value={filters.keshte}
              onChange={(e) => handleFilterChange("keshte", e.target.value)}
              className="dashboard-select"
            >
              <MenuItem value="">همه</MenuItem>
              {keshteOptions.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              select
              label="واحد عملیاتی"
              value={filters.production}
              onChange={(e) => handleFilterChange("production", e.target.value)}
              className="dashboard-select"
            >
              {productionOptionsState.map((item) => (
                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
              ))}
            </TextField>

            {showDepartment && (
              <TextField
                variant="outlined"
                size="small"
                select
                label="نام بخش"
                value={filters.department}
                onChange={(e) => handleFilterChange("department", e.target.value)}
                className="dashboard-select span-2"
              >
                <MenuItem value="">همه</MenuItem>
                {unitOptions.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </TextField>
            )}

            {showSearch && (
              <TextField
                variant="outlined"
                size="small"
                label={filters.production === "block" ? "نام/کد بلوک" : "نام/کد مزرعه"}
                placeholder="جستجو کنید..."
                value={filters.searchQuery}
                onChange={(e) => handleFilterChange("searchQuery", e.target.value)}
                className="dashboard-select span-2"
              />
            )}

            <TextField
              variant="outlined"
              size="small"
              select
              label="نوع محصول"
              value={filters.productType}
              onChange={(e) => handleFilterChange("productType", e.target.value)}
              className="dashboard-select"
            >
              <MenuItem value="">همه</MenuItem>
              {croptypeOptions.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              select
              label="رقم"
              value={filters.number}
              onChange={(e) => handleFilterChange("number", e.target.value)}
              className="dashboard-select"
            >
              <MenuItem value="">همه</MenuItem>
              {varietyOptions.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              select
              label="سن کشت"
              value={filters.age}
              onChange={(e) => handleFilterChange("age", e.target.value)}
              className="dashboard-select"
            >
              <MenuItem value="">همه</MenuItem>
              {ageOptions.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              select
              label="وضعیت"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="dashboard-select"
            >
              <MenuItem value="">همه</MenuItem>
              {statusOptions.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </div>

          <div className="dashboard-filter-footer">
            <div className="dashboard-date-presets">
              {quickDateRanges.map((range) => (
                <Button
                  key={range.label}
                  variant="outlined"
                  className="date-preset-button"
                  onClick={() => handleQuickDateRange(range)}
                >
                  {range.label}
                </Button>
              ))}
            </div>
            <Button
              variant="outlined"
              startIcon={<CalendarMonth />}
              className="secondary-action-button"
              onClick={() => setIsTimeFilterOpen(true)}
            >
              {dateLabel}
            </Button>
          </div>
          <div className="dashboard-filter-summary">
            <strong>محدوده مکانی:</strong> {spatialLabel}
            {selectedSpatialFilters && (
              <div className="filter-chip-row">
                {[
                  selectedSpatialFilters.keshte,
                  selectedSpatialFilters.production,
                  selectedSpatialFilters.productType,
                  selectedSpatialFilters.number,
                  selectedSpatialFilters.age,
                  selectedSpatialFilters.status,
                  selectedSpatialFilters.department || selectedSpatialFilters.searchQuery,
                ]
                  .filter(Boolean)
                  .map((chip) => (
                    <span key={chip} className="filter-chip">
                      {chip}
                    </span>
                  ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section-card stats-section">
        <div className="section-header">
          <div className="header-title-group">
            <div className="title-icon">📈</div>
            <h2 className="section-title">آمار کلی</h2>
          </div>
          <div className="action-buttons">
            <Button variant="outlined" className="filter-action-button" onClick={loadDashboardData} startIcon={<CheckCircle />}>به‌روزرسانی</Button>
          </div>
        </div>
        <div className="section-content">
          <div className="stats-grid">
            {statCards.map((item) => (
              <div key={item.label} className="stat-card">
                <div className="stat-label">{item.label}</div>
                <div className="circle-container">
                  <div className="floating-icon">{item.icon}</div>
                  <div className="progress-ring" style={{ "--p": item.percent }}>
                    <div className="circle-center">
                      {item.value}
                      {typeof item.value === "number" && item.label !== "دما" ? "%" : ""}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-card">
        <div className="section-header">
          <div className="header-title-group">
            <div className="title-icon">📊</div>
            <h2 className="section-title">مقایسه یک شاخص</h2>
          </div>
          <div className="action-buttons">
            <Button variant="outlined" className="filter-action-button" startIcon={<Print />}>چاپ</Button>
            <Button variant="outlined" className="filter-action-button" startIcon={<Download />}>دانلود</Button>
          </div>
        </div>

        <div className="section-content">
          <div className="chart-settings-row">
            <TextField
              variant="outlined"
              size="small"
              select
              label="شاخص اصلی"
              value={primaryIndicator}
              onChange={(e) => handlePrimaryIndicatorChange(e.target.value)}
              className="dashboard-select"
            >
              {indicatorOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              variant="outlined"
              size="small"
              select
              label="شاخص ثانویه"
              value={secondaryIndicator}
              onChange={(e) => handleSecondaryIndicatorChange(e.target.value)}
              className="dashboard-select"
            >
              {indicatorOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              className="primary-action-button"
              onClick={loadDashboardData}
              startIcon={<CheckCircle />}
            >
              اعمال
            </Button>
          </div>
          <div className="chart-top-bar">
            <div className="chart-info">داده‌های شاخص {primaryIndicator} برای {spatialLabel}</div>
            <div className="chart-utils">
              <Download fontSize="small" />
              <Print fontSize="small" />
            </div>
          </div>
          <div className="bar-chart-area">
            <div className="chart-wrapper">
              <span className="y-axis-label">{primaryIndicator}</span>
              <div className="chart-canvas real-chart">
                {isLoading ? (
                  <div className="loader-placeholder"><CircularProgress /></div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                      <XAxis dataKey="name" tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Yekan Bakh FaNum", borderRadius: 8 }} />
                      <Bar dataKey={primaryIndicator} fill="#344E41" radius={[8, 8, 0, 0]} barSize={36} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-card">
        <div className="section-header">
          <div className="header-title-group">
            <div className="title-icon">📈</div>
            <h2 className="section-title">مقایسه دو شاخص</h2>
          </div>
        </div>
        <div className="section-content">
          <div className="chart-top-bar">
            <div className="chart-info">مقایسه {primaryIndicator} و {secondaryIndicator}</div>
          </div>
          <div className="bar-chart-area">
            <div className="chart-wrapper">
              <span className="y-axis-label">مقدار</span>
              <div className="chart-canvas real-chart">
                {isLoading ? (
                  <div className="loader-placeholder"><CircularProgress /></div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={doubleData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                      <XAxis dataKey="name" tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Yekan Bakh FaNum", borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontFamily: "Yekan Bakh FaNum", fontSize: 12 }} />
                      <Bar dataKey={primaryIndicator} fill="#344E41" radius={[8, 8, 0, 0]} barSize={28} />
                      <Bar dataKey={secondaryIndicator} fill="#95A176" radius={[8, 8, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-card">
        <div className="section-header">
          <div className="header-title-group">
            <div className="title-icon">📉</div>
            <h2 className="section-title">روند زمانی شاخص</h2>
          </div>
        </div>
        <div className="section-content">
          <div className="chart-top-bar">
            <div className="chart-info">روند زمانی {primaryIndicator} در بازه {dateLabel}</div>
          </div>
          <div className="bar-chart-area">
            <div className="chart-wrapper">
              <span className="y-axis-label">{primaryIndicator}</span>
              <div className="chart-canvas real-chart">
                {isLoading ? (
                  <div className="loader-placeholder"><CircularProgress /></div>
                ) : timeSeriesData.length === 0 ? (
                  <div className="no-data-container">داده‌ای برای نمایش وجود ندارد.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={timeSeriesData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Yekan Bakh FaNum", borderRadius: 8 }} />
                      <Line type="monotone" dataKey="value" stroke="#344E41" strokeWidth={3} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-card">
        <div className="section-header">
          <div className="header-title-group">
            <div className="title-icon">🧾</div>
            <h2 className="section-title">جدول مقایسه‌ای</h2>
          </div>
        </div>
        <div className="section-content">
          <div className="table-action-row">
            <Button
              variant="outlined"
              className="filter-action-button"
              startIcon={<Download />}
              onClick={downloadCsv}
              disabled={!tableData.length}
            >
              خروجی CSV
            </Button>
            <Button
              variant="outlined"
              className="filter-action-button"
              startIcon={<Print />}
            >
              چاپ جدول
            </Button>
            <div className="table-summary">
              {tableData.length ? `${tableData.length} ردیف نمایش داده شد` : "جدولی برای نمایش وجود ندارد."}
            </div>
          </div>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>کشت و صنعت</th>
                  <th>{primaryIndicator}</th>
                  <th>{secondaryIndicator}</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan={3}>داده‌ای برای نمایش وجود ندارد.</td>
                  </tr>
                ) : (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.name}</td>
                      <td>{row[primaryIndicator.toLowerCase()] ?? "—"}</td>
                      <td>{row[secondaryIndicator.toLowerCase()] ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <TimeFilterDialog
        open={isTimeFilterOpen}
        onClose={() => setIsTimeFilterOpen(false)}
        onConfirm={handleTimeFilterConfirm}
        onClear={handleTimeFilterClear}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      <SpatialFilterModal
        isOpen={isSpatialModalOpen}
        onClose={() => setIsSpatialModalOpen(false)}
        onApplyFilter={handleDashboardSpatialFilterApply}
        initialFilter={selectedSpatialFilters}
      />
    </div>
  );
};

export default Dashboard;